import { Type, type Static } from "@sinclair/typebox";
export * from "./planning-weather.js";
export * from "./password-policy.js";

export const UuidSchema = Type.String({ format: "uuid" });
export const IsoDateTimeSchema = Type.String({ format: "date-time" });

export const ApiErrorSchema = Type.Object(
  {
    error: Type.Object({
      code: Type.String(),
      message: Type.String(),
      correlationId: UuidSchema,
      details: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
    }),
  },
  { $id: "ApiError", additionalProperties: false },
);

export const HealthSchema = Type.Object(
  {
    status: Type.Union([Type.Literal("ok"), Type.Literal("degraded")]),
    service: Type.Literal("gestionale-cem-api"),
    version: Type.String(),
    readinessContract: Type.Literal(1),
    sourceVersion: Type.String({ minLength: 1 }),
    database: Type.Union([
      Type.Literal("up"),
      Type.Literal("down"),
      Type.Literal("not-checked"),
    ]),
    schema: Type.Union([
      Type.Literal("current"),
      Type.Literal("pending"),
      Type.Literal("invalid"),
      Type.Literal("not-checked"),
    ]),
    schemaVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    requiredSchemaVersion: Type.Union([
      Type.Integer({ minimum: 1 }),
      Type.Null(),
    ]),
    pendingMigrations: Type.Array(Type.Integer({ minimum: 1 })),
    message: Type.Optional(Type.String()),
    timestamp: IsoDateTimeSchema,
  },
  { $id: "Health", additionalProperties: false },
);

export const LoginRequestSchema = Type.Object(
  {
    username: Type.String({ minLength: 1, maxLength: 254 }),
    password: Type.String({ minLength: 12, maxLength: 1024 }),
  },
  { $id: "LoginRequest", additionalProperties: false },
);

export const PasswordChangeRequestSchema = Type.Object(
  {
    currentPassword: Type.String({ minLength: 12, maxLength: 1024 }),
    newPassword: Type.String({ minLength: 12, maxLength: 1024 }),
  },
  { $id: "PasswordChangeRequest", additionalProperties: false },
);

export const AuthenticatedUserSchema = Type.Object(
  {
    id: UuidSchema,
    username: Type.String(),
    displayName: Type.String(),
    permissions: Type.Array(Type.String()),
  },
  { $id: "AuthenticatedUser", additionalProperties: false },
);

export const SessionResponseSchema = Type.Object(
  {
    user: AuthenticatedUserSchema,
    mustChangePassword: Type.Boolean(),
    csrfToken: Type.String({ minLength: 32 }),
    expiresAt: IsoDateTimeSchema,
  },
  { $id: "SessionResponse", additionalProperties: false },
);

export const PaginationQuerySchema = Type.Object(
  {
    cursor: Type.Optional(Type.String({ minLength: 1, maxLength: 512 })),
    limit: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 200, default: 50 }),
    ),
  },
  { additionalProperties: false },
);

export type LoginRequest = Static<typeof LoginRequestSchema>;
export type PasswordChangeRequest = Static<typeof PasswordChangeRequestSchema>;
export type AuthenticatedUser = Static<typeof AuthenticatedUserSchema>;
export type SessionResponse = Static<typeof SessionResponseSchema>;

export * from "./directories.js";
export * from "./billing.js";
export * from "./catalog.js";
export * from "./economics.js";
export * from "./operations.js";
export * from "./personal.js";
export * from "./recovery.js";
export * from "./settings.js";
export * from "./fic-sync.js";
export * from "./fic-identity.js";
export * from "./accounts.js";
export * from "./lifelines.js";
export * from "./recurring.js";
export * from "./dashboard-cards.js";
export * from "./internal-costs.js";
export * from "./asset-installments.js";

export * from "./archive.js";

export * from "./attendance.js";
export * from "./lifeline-inspections.js";
export * from "./assistant.js";

export * from "./personal-agenda.js";

export * from "./lifeline-billing.js";

export * from "./technical-reports.js";

export * from "./forms.js";

export * from "./employee-document-deadlines.js";

export * from "./structure.js";

export * from "./business-simulation.js";

export * from "./structure-invoice-integration.js";

export * from "./supplier-destinations.js";

export * from "./production-capacity.js";
export * from "./structure-organization.js";
export * from "./structure-year-reset.js";
export * from "./administrator-transfers.js";

export * from "./whatsapp.js";

export * from "./archive-directory-sync.js";
export * from "./asset-qr.js";

export * from "./referral-commissions.js";
export * from "./server-status.js";
export * from "./supplier-purchases.js";
export * from "./customer-portal.js";

export * from "./customer-sharing.js";
